// SignalDetector.js — My Home Buddha Portal Assistant
// Detects signals from user data + mybuddha records, and always generates
// a full daily briefing with actionable suggestions even when no alerts exist.

// ── Constants ─────────────────────────────────────────────────────────────────
export const PRIORITY = { URGENT: "urgent", HIGH: "high", MEDIUM: "medium", LOW: "low", INFO: "info" };

const AI_SITE   = "https://ai.myhomebuddha.com";
const MAIN_SITE = "https://www.myhomebuddha.com";
const STRIPE_HEALTH_CHECK = "https://buy.stripe.com/8x26oGdxYa1zcc54ta8og1J";

// ── Signal type configs (icon + color for each category) ─────────────────────
export const SIGNAL_CONFIG = {
  // Homeowner / maintenance
  maintenance:      { icon: "🔧", color: "#D97706" },
  water_heater:     { icon: "🚿", color: "#E53E3E" },
  hvac:             { icon: "❄️",  color: "#3182CE" },
  roof:             { icon: "🏠", color: "#805AD5" },
  septic:           { icon: "⚠️", color: "#E53E3E" },
  seasonal:         { icon: "🍂", color: "#D97706" },
  // Value / financial
  value:            { icon: "📈", color: "#2D6A4F" },
  equity:           { icon: "💰", color: "#2D6A4F" },
  // Profile / completion
  profile:          { icon: "👤", color: "#1A2280" },
  branding:         { icon: "🎨", color: "#1A2280" },
  // Network / leads
  network:          { icon: "🤝", color: "#D4921A" },
  leads:            { icon: "⚡", color: "#E53E3E" },
  // Content
  content:          { icon: "✍️", color: "#805AD5" },
  // AI tools
  ai_tools:         { icon: "🤖", color: "#1A2280" },
  // General suggestions
  suggestion:       { icon: "💡", color: "#2D6A4F" },
  invite:           { icon: "👋", color: "#D4921A" },
  health_check:     { icon: "🏡", color: "#2D6A4F" },
  market:           { icon: "🔍", color: "#3182CE" },
};

// ── Cache helpers ─────────────────────────────────────────────────────────────
const CACHE_KEY = (uid) => `mhb_signals_${uid}`;
const CACHE_TTL = 15 * 60 * 1000; // 15 minutes

export function getCachedSignals(userId) {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY(userId));
    if (!raw) return null;
    const { ts, data } = JSON.parse(raw);
    if (Date.now() - ts > CACHE_TTL) return null;
    return data;
  } catch { return null; }
}

export function setCachedSignals(userId, signals) {
  try {
    sessionStorage.setItem(CACHE_KEY(userId), JSON.stringify({ ts: Date.now(), data: signals }));
  } catch {}
}

export function clearSignalCache(userId) {
  try { sessionStorage.removeItem(CACHE_KEY(userId)); } catch {}
}

export function getSignalCount(signals) {
  return (signals || []).filter(s => ["urgent", "high"].includes(s.priority)).length;
}

export function getPrioritySignals(signals, n = 3) {
  const order = { urgent: 0, high: 1, medium: 2, low: 3, info: 4 };
  return [...(signals || [])].sort((a, b) => (order[a.priority] || 4) - (order[b.priority] || 4)).slice(0, n);
}

// ── Signal builder helper ─────────────────────────────────────────────────────
let _sigId = 0;
function sig(type, priority, title, body, opts = {}) {
  return {
    id:          `${type}_${++_sigId}_${Date.now()}`,
    type,
    priority,
    title,
    body,
    dismissible: opts.dismissible !== false,
    cta:         opts.cta || null,
  };
}
function cta(label, url, action = null) { return { label, url, action }; }

// ── Agent invite link builder ─────────────────────────────────────────────────
function agentInviteUrl(user) {
  const slug = agentSlug(user);
  const uid  = user?.id || user?.userId || "";
  return `${AI_SITE}/${slug}${uid ? `?uid=${encodeURIComponent(uid)}` : ""}`;
}
function agentInviteUrlMode(user, mode) {
  const slug = agentSlug(user);
  const uid  = user?.id || user?.userId || "";
  return `${AI_SITE}/${slug}/${mode}${uid ? `?uid=${encodeURIComponent(uid)}` : ""}`;
}
function agentSlug(user) {
  if (!user) return "agent";
  const first = (user.firstName || "").trim().replace(/[^a-zA-Z0-9]/g, "");
  const last  = (user.lastName  || "").trim().replace(/[^a-zA-Z0-9]/g, "");
  if (first && last) return first + last;
  const name = (user.name || "").trim().replace(/[^a-zA-Z0-9 ]/g, "");
  if (name.includes(" ")) return name.split(/\s+/).map(w => w.charAt(0).toUpperCase() + w.slice(1)).join("");
  return (user.email || "agent").split("@")[0].replace(/[^a-zA-Z0-9]/g, "").slice(0, 16) || "agent";
}

// ── Main detector ─────────────────────────────────────────────────────────────
export function detectSignals(userDetails, records = []) {
  _sigId = 0; // reset per call
  const signals  = [];
  const profile  = userDetails?.userProfile || "B";
  const isAgent  = ["AG", "LN"].includes(profile);
  const isLender = profile === "L";
  const isBuyer  = profile === "B";
  const isSeller = profile === "S";
  const isOwner  = profile === "HO";
  const isVendor = profile === "V";

  // ── 1. PROFILE COMPLETENESS ────────────────────────────────────────────────
  if (isAgent || isLender) {
    const nd = userDetails?.newdata || {};
    const missing = [];
    if (!nd.companyName)                    missing.push("company name");
    if (!nd.titleorPosition)                missing.push("title");
    if (!nd.website)                        missing.push("website");
    if (!userDetails?.profilePicture?.length) missing.push("profile photo");
    if (!userDetails?.userBio)              missing.push("bio");
    if (missing.length >= 3) {
      signals.push(sig("profile", PRIORITY.HIGH,
        "Complete Your Agent Profile",
        `Your profile is missing: ${missing.slice(0, 3).join(", ")}. A complete profile builds trust and increases referrals.`,
        { cta: cta("Complete Profile", `${MAIN_SITE}/profile`) }
      ));
    } else if (missing.length > 0) {
      signals.push(sig("profile", PRIORITY.MEDIUM,
        "Profile Almost Complete",
        `Just add your ${missing.join(" and ")} to finish your profile.`,
        { cta: cta("Finish Profile", `${MAIN_SITE}/profile`) }
      ));
    }

    // Branding not set up
    const hasBranding = nd.companyName && userDetails?.profilePicture?.length;
    if (!hasBranding) {
      signals.push(sig("branding", PRIORITY.MEDIUM,
        "Set Up Your Branded AI Tools",
        "Add your photo and company name so your AI share links show your branding to every client who clicks them.",
        { cta: cta("Set Up Branding", `${MAIN_SITE}/profile`) }
      ));
    }
  }

  // ── 2. MYBUDDHA RECORD SIGNALS ────────────────────────────────────────────
  const now         = Date.now();
  const currentYear = new Date().getFullYear();
  const DAY         = 86400000;

  // Look for home health check-up records, reminders, maintenance items
  const healthRecords  = records.filter(r => r.recordType === "home_health_entry" || r.section === "home_health");
  const valueRecords   = records.filter(r => r.recordType === "value_wealth" || r.section === "value_wealth");
  const reminderRecords = records.filter(r => r.recordType === "reminder" && r.status === "pending");
  const allEntries     = records.filter(r => r.recordType === "checklist_entry" || r.recordType === "home_health_entry");

  // Overdue reminders
  reminderRecords
    .filter(r => r.scheduledFor && r.scheduledFor < now)
    .slice(0, 3)
    .forEach(r => {
      const daysOverdue = Math.floor((now - r.scheduledFor) / DAY);
      signals.push(sig(
        r.reminderType || "maintenance",
        daysOverdue > 30 ? PRIORITY.URGENT : PRIORITY.HIGH,
        r.title || "Maintenance Reminder Overdue",
        `${r.message || "This item is past due."} (${daysOverdue} day${daysOverdue !== 1 ? "s" : ""} overdue)`,
        { cta: r.ctaUrl ? cta(r.ctaLabel || "Take Action", r.ctaUrl) : null }
      ));
    });

  // Upcoming reminders (within 14 days)
  reminderRecords
    .filter(r => r.scheduledFor && r.scheduledFor >= now && r.scheduledFor < now + 14 * DAY)
    .slice(0, 2)
    .forEach(r => {
      const daysAway = Math.ceil((r.scheduledFor - now) / DAY);
      signals.push(sig(
        r.reminderType || "maintenance",
        PRIORITY.MEDIUM,
        r.title || "Upcoming Maintenance",
        `Due in ${daysAway} day${daysAway !== 1 ? "s" : ""}. ${r.message || ""}`,
        { cta: r.ctaUrl ? cta(r.ctaLabel || "View Details", r.ctaUrl) : null }
      ));
    });

  // Home health: check for aging systems logged in checklist entries
  allEntries.forEach(entry => {
    const installYear = parseInt(entry.year || entry.installYear || 0);
    if (!installYear) return;
    const age = currentYear - installYear;
    const cat = (entry.category || entry.subsection || "").toLowerCase();

    if ((cat.includes("water") && cat.includes("heat")) || cat === "water_heater") {
      if (age >= 10) signals.push(sig("water_heater", age >= 13 ? PRIORITY.URGENT : PRIORITY.HIGH,
        `Water Heater ${age >= 13 ? "Past" : "Nearing"} End of Life`,
        `Your water heater is ${age} years old. Average lifespan is 10–12 years. Consider proactive replacement to avoid emergency costs.`,
        { cta: cta("Book a Home Health Check", STRIPE_HEALTH_CHECK) }
      ));
    }
    if (cat.includes("hvac") || cat.includes("furnace") || cat.includes("heat pump")) {
      if (age >= 15) signals.push(sig("hvac", age >= 20 ? PRIORITY.HIGH : PRIORITY.MEDIUM,
        `HVAC System ${age} Years Old`,
        `HVAC systems typically last 15–20 years. Yours may be approaching replacement age — a tune-up now can extend its life.`,
        { cta: cta("Schedule a Check-Up", STRIPE_HEALTH_CHECK) }
      ));
    }
    if (cat.includes("roof")) {
      if (age >= 20) signals.push(sig("roof", age >= 25 ? PRIORITY.URGENT : PRIORITY.HIGH,
        `Roof May Need Inspection`,
        `Your roof is approximately ${age} years old. Most roofs last 20–30 years. A professional inspection can catch issues before they become costly.`,
        { cta: cta("Book Home Health Check", STRIPE_HEALTH_CHECK) }
      ));
    }
  });

  // Septic
  allEntries.forEach(entry => {
    const cat = (entry.category || entry.subsection || "").toLowerCase();
    if (!cat.includes("septic")) return;
    const lastPumped = parseInt(entry.year || entry.lastPumped || 0);
    if (lastPumped && currentYear - lastPumped >= 3) {
      signals.push(sig("septic", PRIORITY.HIGH,
        "Septic Tank Pumping Due",
        `Your septic was last pumped ${currentYear - lastPumped} years ago. Most systems need pumping every 3–5 years to avoid costly failures.`,
        { cta: cta("Find a Pro", `${MAIN_SITE}/find-pros`) }
      ));
    }
  });

  // ── 3. SEASONAL SIGNALS ───────────────────────────────────────────────────
  const month = new Date().getMonth(); // 0-indexed
  if (isOwner || isBuyer) {
    if (month === 8 || month === 9) { // Sept / Oct
      signals.push(sig("seasonal", PRIORITY.MEDIUM,
        "Fall Home Prep Checklist",
        "September and October are ideal for HVAC servicing, weatherstripping, gutter cleaning, and winterizing pipes before the cold hits.",
        { cta: cta("View Checklist", `${MAIN_SITE}/homeowner`) }
      ));
    }
    if (month === 2 || month === 3) { // March / April
      signals.push(sig("seasonal", PRIORITY.LOW,
        "Spring Home Check-In",
        "Spring is the best time to inspect your roof, clean gutters, test smoke detectors, and service your AC before summer.",
        { cta: cta("Start Home Check-In", `${AI_SITE}/homeowner`) }
      ));
    }
  }

  // ── 4. DAILY SUGGESTIONS — always shown, never empty ─────────────────────
  // These ensure the panel always feels valuable even with no alerts.

  if (isAgent) {
    // Core agent growth actions
    signals.push(sig("ai_tools", PRIORITY.INFO,
      "Share Your Branded AI Tools",
      "Send clients your branded buyer, seller, or home health links. Every visitor sees your name and photo — and new signups go straight into your network.",
      { cta: cta("Get My Share Links", `${MAIN_SITE}/agent-tools`) }
    ));
    signals.push(sig("invite", PRIORITY.INFO,
      "Invite Past Clients — Free Home Value Tracker",
      "Every past client gets a free home management portal. Send them your branded seller link and stay top of mind as their equity grows.",
      { cta: cta("Send Seller Link", agentInviteUrlMode(userDetails, "seller")) }
    ));
    signals.push(sig("health_check", PRIORITY.INFO,
      "Gift Clients a Home Health Check-Up",
      "Send buyers or homeowners your branded home health check link. It's a $250 service that builds massive trust and keeps you in their home forever.",
      { cta: cta("Share Health Check Link", agentInviteUrlMode(userDetails, "healthcheck")) }
    ));
    signals.push(sig("network", PRIORITY.INFO,
      "Add Contacts & Never Miss a Touch Point",
      "Build your network in My Home Buddha. Contacts with homes trigger automatic value and maintenance alerts — keeping you relevant year-round.",
      { cta: cta("Open My Network", `${MAIN_SITE}/my-network`) }
    ));
    signals.push(sig("content", PRIORITY.INFO,
      "Generate Today's Social Content",
      "Use the AI Content Studio to create a market update, listing description, or neighborhood guide in your voice — in under 60 seconds.",
      { cta: cta("Open Content Studio", `${MAIN_SITE}/content-studio`) }
    ));
    signals.push(sig("market", PRIORITY.INFO,
      "Research Any Property with AI",
      "Run a full AI buyer or seller analysis on any address — health flags, value, comps, neighborhood, utilities, and more.",
      { cta: cta("Research a Property", agentInviteUrlMode(userDetails, "buyer")) }
    ));
  }

  if (isLender) {
    signals.push(sig("ai_tools", PRIORITY.INFO,
      "Share Your Branded AI Readiness Tool",
      "Send buyers your branded home buying readiness check. They get a personalized score and action plan — you get them in your network.",
      { cta: cta("Get Readiness Link", agentInviteUrlMode(userDetails, "readiness")) }
    ));
    signals.push(sig("invite", PRIORITY.INFO,
      "Invite Agent Partners — Build Your Referral Network",
      "Connect with agents on My Home Buddha. Your profile appears on their listings and they can refer buyers directly to you.",
      { cta: cta("Invite an Agent", `${MAIN_SITE}/my-network`) }
    ));
    signals.push(sig("network", PRIORITY.INFO,
      "Stay Visible to Active Buyers",
      "Buyers researching properties on My Home Buddha can see lenders in their area. Keep your profile complete so you show up first.",
      { cta: cta("Update My Profile", `${MAIN_SITE}/profile`) }
    ));
    signals.push(sig("market", PRIORITY.INFO,
      "Research Any Property with AI",
      "Run a full AI buyer analysis on any property — value, comps, health flags, and 5-year ownership cost estimate.",
      { cta: cta("Research a Property", `${AI_SITE}/buyer`) }
    ));
  }

  if (isBuyer) {
    signals.push(sig("market", PRIORITY.INFO,
      "Research Any Home You're Considering",
      "Enter any address to get public records, market value, comparable sales, health & risk flags, neighborhood data, and an inspection checklist.",
      { cta: cta("Research a Home", `${AI_SITE}/buyer`) }
    ));
    signals.push(sig("ai_tools", PRIORITY.INFO,
      "Know Your Buying Readiness Score",
      "Answer 8 quick questions to see your readiness score, buying power range, and a personalized action plan — free, no credit check.",
      { cta: cta("Check My Readiness", `${AI_SITE}/readiness`) }
    ));
    signals.push(sig("value", PRIORITY.INFO,
      "Track Your Future Home's Value",
      "Once you close, your Home Buddha portal tracks equity, maintenance, and value — keeping you wealthy for life.",
      { cta: cta("Learn About the Portal", `${MAIN_SITE}/homeowner`) }
    ));
    signals.push(sig("invite", PRIORITY.INFO,
      "Know Someone Else Buying or Selling?",
      "Share My Home Buddha with friends and family — it's free to use and gives everyone the same AI tools you have.",
      { cta: cta("Share My Home Buddha", `${AI_SITE}`) }
    ));
  }

  if (isSeller) {
    signals.push(sig("value", PRIORITY.INFO,
      "See What Buyers Will Find About Your Home",
      "Run a full seller analysis to see your public records, verified AVM value, what inspectors will flag, and top ROI improvements.",
      { cta: cta("Get My Home Value", `${AI_SITE}/seller`) }
    ));
    signals.push(sig("health_check", PRIORITY.INFO,
      "Build a Home Facts Report for Your Listing",
      "A certified My Home Buddha Home Facts Report gives buyers confidence and reduces disclosure risk. Agents love it.",
      { cta: cta("Learn About Home Facts", STRIPE_HEALTH_CHECK) }
    ));
    signals.push(sig("market", PRIORITY.INFO,
      "See Comparable Sales in Your Area",
      "Research nearby sold homes to understand your pricing position before listing.",
      { cta: cta("Research Comps", `${AI_SITE}/seller`) }
    ));
    signals.push(sig("invite", PRIORITY.INFO,
      "Know Someone Buying or Selling?",
      "Share My Home Buddha — free AI tools for buyers, sellers, and homeowners.",
      { cta: cta("Share My Home Buddha", `${AI_SITE}`) }
    ));
  }

  if (isOwner) {
    signals.push(sig("value", PRIORITY.INFO,
      "Check Your Home's Value Today",
      "Run a quick seller analysis to see your current AVM value, equity estimate, and top improvements that boost your sale price.",
      { cta: cta("Check My Value", `${AI_SITE}/seller`) }
    ));
    signals.push(sig("health_check", PRIORITY.INFO,
      "Book Your Annual Home Health Check-Up",
      "A certified advisor visits, ages all your major systems, and sets you up with a lifetime maintenance plan. Just $250 — pays for itself.",
      { cta: cta("Book Health Check", STRIPE_HEALTH_CHECK) }
    ));
    signals.push(sig("maintenance", PRIORITY.INFO,
      "Log a Recent Home Improvement",
      "Every improvement you log in your portal adds to your home's documented value and maintenance history.",
      { cta: cta("Update My Portal", `${MAIN_SITE}/homeowner`) }
    ));
    signals.push(sig("invite", PRIORITY.INFO,
      "Know Other Homeowners?",
      "Share My Home Buddha — every homeowner gets a free AI home management portal to track their value and maintenance.",
      { cta: cta("Share with a Neighbor", `${AI_SITE}`) }
    ));
  }

  if (isVendor) {
    signals.push(sig("profile", PRIORITY.INFO,
      "Keep Your Service Profile Current",
      "Agents and homeowners search for trusted vendors on My Home Buddha. A complete profile with your services and contact info gets you found.",
      { cta: cta("Update My Profile", `${MAIN_SITE}/profile`) }
    ));
    signals.push(sig("network", PRIORITY.INFO,
      "Connect with Local Agents",
      "Build relationships with agents who refer clients regularly. My Home Buddha's network lets them feature you to every buyer and seller.",
      { cta: cta("Explore My Network", `${MAIN_SITE}/my-network`) }
    ));
    signals.push(sig("invite", PRIORITY.INFO,
      "Know a Homeowner Who Needs Your Services?",
      "Share My Home Buddha — when they log in and add their home, your service category appears as a recommended resource.",
      { cta: cta("Share My Home Buddha", `${AI_SITE}`) }
    ));
  }

  // Universal — everyone gets this
  signals.push(sig("invite", PRIORITY.INFO,
    "Know Someone Buying, Selling, or Managing a Home?",
    "My Home Buddha is free for everyone. Share it and help someone make a smarter home decision today.",
    { cta: cta("Share My Home Buddha", `${AI_SITE}`) }
  ));

  // ── 5. SORT — urgent → high → medium → low → info ────────────────────────
  const order = { urgent: 0, high: 1, medium: 2, low: 3, info: 4 };
  signals.sort((a, b) => (order[a.priority] ?? 4) - (order[b.priority] ?? 4));

  return signals;
}